import type {
  FormComponent,
  FormDSLContext,
  ValidationRules,
} from '@commenda/types';

/**
 * Synthetic file value used to satisfy required-field validation for file
 * fields in preview. The `preview://` scheme is recognised by FormSummary so it
 * never attempts a signed-URL fetch for a file that was never uploaded.
 */
export const PREVIEW_FILE_URL_SCHEME = 'preview://';

const SYNTHETIC_FILE_VALUE = {
  fileName: 'test-document.pdf',
  fileUrl: `${PREVIEW_FILE_URL_SCHEME}test-document`,
  documentType: 'application/pdf',
};

/**
 * Outcome of asking the registry for a test value for one component:
 * - `value`: a concrete value to write into formData
 * - `skip`: cannot fill, and the reason is worth reporting to the tester
 * - `silent-skip`: intentionally not filled, not worth reporting (display-only,
 *   read-only OS fields)
 */
export type TestValueResult =
  | { kind: 'value'; value: unknown }
  | { kind: 'skip'; reason: string }
  | { kind: 'silent-skip' };

const isoDateDaysAgo = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
};

const TODAY_ISO = (): string => isoDateDaysAgo(0);

function firstStaticOptionValue(
  component: FormComponent
): string | undefined {
  const { options } = component;
  if (!Array.isArray(options) || options.length === 0) return undefined;
  const [firstOption] = options;
  return typeof firstOption?.value === 'string' ? firstOption.value : undefined;
}

// A component drives its options from a Content Library / Commenda OS data
// source rather than a static `options` list. Those options load inside the
// wrapper at render time and are not observable here, so such fields are
// skipped rather than guessed.
function hasDynamicDataSource(component: FormComponent): boolean {
  return Boolean(
    component.dataSource?.contentLibrary || component.dataSource?.commendaOS
  );
}

const SCALAR_VALUES: Record<string, unknown> = {
  Input: 'Test value',
  TextArea: 'This is a sample response for preview testing.',
  NumberInput: 1,
  UrlInput: 'https://example.com',
  PasswordInput: 'TestPassword123!',
  // E.164: no spaces, or the phone input rejects it as an initial value.
  PhoneInput: '+15550000000',
  CountryInput: 'US',
};

// Repeatable-module instances get per-instance-distinct values where the
// format allows, so `uniqueInRepeatable`/`notSameAs` rules don't fail on
// duplicates the fill itself manufactured. Format-constrained types (phone,
// country, password) keep the shared sample.
function variedScalarValue(type: string, instanceIndex?: number): unknown {
  const base = SCALAR_VALUES[type];
  if (!instanceIndex) return base;
  if (typeof base === 'number') return base + instanceIndex;
  if (type === 'UrlInput') return `${base}/${instanceIndex + 1}`;
  if (type === 'Input' || type === 'TextArea') {
    return `${base} ${instanceIndex + 1}`;
  }
  return base;
}

// Respect format rules the generic sample would violate: an Input with an
// email/url/phone rule needs a value of that shape, or pre-fill guarantees a
// validation failure.
function scalarTestValue(
  type: string,
  rules: ValidationRules | undefined,
  instanceIndex?: number
): unknown {
  if (rules?.email) {
    return instanceIndex
      ? `test${instanceIndex + 1}@example.com`
      : 'test@example.com';
  }
  if (rules?.url) return variedScalarValue('UrlInput', instanceIndex);
  if (rules?.phone) return SCALAR_VALUES.PhoneInput;
  return variedScalarValue(type, instanceIndex);
}

// AddressInput stores a structured object (see wrappers/AddressInput.tsx
// normalizeAddress) — a plain string would pass required-validation while the
// widget renders empty sub-fields. Also exported for wrapper self-fill
// handlers that synthesize an address.
export const ADDRESS_TEST_VALUE = {
  addressLine1: '1 Test Street',
  city: 'San Francisco',
  state: 'CA',
  postalCode: '94105',
  country: 'US',
};

// Sample data for wrapper self-fill handlers (registerPreviewFillHandler) that
// synthesize a new record in preview instead of selecting an existing one.
// Kept here so every preview test value lives in this one registry.

export const REGISTERED_AGENT_TEST_NAME = 'Test registered agent';

// Candidates covering common identifier formats (EIN with/without hyphen,
// alphanumeric); GovernmentIdentifier picks the first one matching its own
// regexPattern.
export const GOVERNMENT_IDENTIFIER_TEST_CANDIDATES = [
  '12-3456789',
  '123456789',
  'TEST12345',
];

export function getLocationTestValue(): {
  name: string;
  address: typeof ADDRESS_TEST_VALUE;
  startDate: string;
} {
  return {
    name: 'Test location',
    address: { ...ADDRESS_TEST_VALUE },
    startDate: TODAY_ISO(),
  };
}

const OS_ENTITY_SKIP_REASON = 'loads real entity data';
const FISCAL_YEAR_TEST_VALUE = '12-31';

/**
 * Component types that fill themselves via registerPreviewFillHandler. Routed
 * to handler execution by TYPE (not by current registration): a collapsed
 * accordion module unmounts its components, so their handlers may not exist
 * yet at planning time — the executor waits for them to mount and register.
 */
export const SELF_FILL_COMPONENT_TYPES = new Set<string>([
  'KeyPersons',
  'BankAccount',
  'AutoDetectField',
  'Location',
  'RegisteredAgent',
  'GovernmentIdentifier',
]);

// Commenda OS entity-backed components: their values reference real records, so
// they can only be filled from the picked corporation's live data inside the
// wrapper — never from a synthetic literal. Skipped-and-reported when reached
// (the EntitySelect family and SELF_FILL types are intercepted by earlier
// branches / handler routing, so this is the fallback for the rest and for
// components inside repeatable instances). Every self-fill type belongs here
// too — derived so the invariant can't drift.
const OS_ENTITY_COMPONENTS = new Set<string>([
  ...Array.from(SELF_FILL_COMPONENT_TYPES),
  'Document',
  'Ownership',
  'RoleAssignment',
  'IndirectTaxRegistration',
]);

// Repeatable sub-entry component. Filling it programmatically is out of scope
// for v1; skipped-and-reported.
const SUBFORM_SKIP_REASON = 'sub-entries are filled manually in preview';
const DYNAMIC_OPTIONS_SKIP_REASON = 'options load at render time';
const FORM_FIELD_OPTIONS_SKIP_REASON = 'options come from another field';
const DYNAMIC_FIELDS_SKIP_REASON = 'fields resolve at render time';

/**
 * The test value (or a skip decision) for a single component, based only on its
 * static DSL shape. Read-only-in-preview handling and "is this field empty /
 * disabled" checks are the caller's responsibility — this function assumes the
 * field is a fillable candidate and only decides what value it would take.
 *
 * `instanceIndex` is the 0-based position inside a repeatable module; later
 * instances get distinct values where the format allows, so uniqueness rules
 * (uniqueInRepeatable) don't fail on duplicates the fill itself created.
 */
export function getTestValueForComponent(
  component: FormComponent,
  dslContext?: FormDSLContext,
  instanceIndex?: number
): TestValueResult {
  const type = component.component;

  if (
    type === 'AutoDetectField' &&
    component.props?.resolvedFieldType === 'fiscalYear'
  ) {
    return { kind: 'value', value: FISCAL_YEAR_TEST_VALUE };
  }

  // EntitySelect stores the selected record's id as a string. When it selects
  // a corporation, the preview picker has already chosen one — filling with
  // that id references a real record, so the no-invented-references rule holds.
  if (type === 'EntitySelect') {
    const entitySlug = component.props?.entity;
    const valueField = component.props?.valueField ?? 'id';
    if (
      entitySlug === 'corporation' &&
      valueField === 'id' &&
      dslContext?.corporationId != null
    ) {
      return { kind: 'value', value: String(dslContext.corporationId) };
    }
    return { kind: 'skip', reason: OS_ENTITY_SKIP_REASON };
  }

  // The SelectOrCreate pair store EXISTING selections as a plain string[] of
  // entity ids (companion `-entity-<id>-data` keys exist only for entities
  // created inline, which pre-fill never does). Both list the picked company's
  // corporations, so the picked corporationId is always a valid member.
  if (
    type === 'EntitySelectOrCreate' ||
    type === 'BusinessEntitySelectOrCreate'
  ) {
    const entitySlug =
      type === 'EntitySelectOrCreate'
        ? (component.props?.entity ?? 'corporation')
        : 'corporation';
    if (entitySlug === 'corporation' && dslContext?.corporationId != null) {
      return { kind: 'value', value: [String(dslContext.corporationId)] };
    }
    return { kind: 'skip', reason: OS_ENTITY_SKIP_REASON };
  }

  // Display-only components hold no value.
  if (type === 'Alert' || type === 'FormSummary') {
    return { kind: 'silent-skip' };
  }

  if (type in SCALAR_VALUES) {
    return {
      kind: 'value',
      value: scalarTestValue(type, component.validation?.rules, instanceIndex),
    };
  }

  if (type === 'DateInput') {
    // Later repeatable instances step one day back per instance, keeping
    // per-instance dates distinct.
    return { kind: 'value', value: isoDateDaysAgo(instanceIndex ?? 0) };
  }

  if (type === 'AddressInput') {
    return { kind: 'value', value: { ...ADDRESS_TEST_VALUE } };
  }

  if (type === 'FileUpload') {
    return { kind: 'value', value: { ...SYNTHETIC_FILE_VALUE } };
  }

  if (type === 'MultiFileUpload') {
    return { kind: 'value', value: [{ ...SYNTHETIC_FILE_VALUE }] };
  }

  if (type === 'DirectorDocument') {
    return {
      kind: 'value',
      value: {
        documentKind:
          typeof component.props?.documentKind === 'string'
            ? component.props.documentKind
            : 'Passport',
        fileName: SYNTHETIC_FILE_VALUE.fileName,
        fileUrl: SYNTHETIC_FILE_VALUE.fileUrl,
      },
    };
  }

  // Same predicates as the Select/RadioGroup wrappers: only a Content Library
  // or form-field source replaces the static options, keyed by its type.
  if (type === 'Select' || type === 'RadioGroup') {
    const { dataSource } = component;
    if (dataSource?.type === 'content-library' && dataSource.contentLibrary) {
      return { kind: 'skip', reason: DYNAMIC_OPTIONS_SKIP_REASON };
    }
    if (dataSource?.type === 'form-field' && dataSource.formField) {
      return { kind: 'skip', reason: FORM_FIELD_OPTIONS_SKIP_REASON };
    }
    const optionValue = firstStaticOptionValue(component);
    return optionValue === undefined
      ? { kind: 'skip', reason: 'no options available' }
      : { kind: 'value', value: optionValue };
  }

  if (type === 'CheckboxGroup') {
    if (hasDynamicDataSource(component)) {
      return { kind: 'skip', reason: DYNAMIC_OPTIONS_SKIP_REASON };
    }
    const optionValue = firstStaticOptionValue(component);
    return optionValue === undefined
      ? { kind: 'skip', reason: 'no options available' }
      : { kind: 'value', value: { [optionValue]: true } };
  }

  if (type === 'DynamicFields') {
    return { kind: 'skip', reason: DYNAMIC_FIELDS_SKIP_REASON };
  }

  if (type === 'NestedForm') {
    return { kind: 'skip', reason: SUBFORM_SKIP_REASON };
  }

  if (OS_ENTITY_COMPONENTS.has(type)) {
    return { kind: 'skip', reason: OS_ENTITY_SKIP_REASON };
  }

  return { kind: 'skip', reason: `unsupported field type (${type})` };
}

/** The schema field an AutoDetectField resolves to and renders from. */
export interface AutoDetectFieldInfo {
  type: string;
  parsingType?: string;
  multiple?: boolean;
  enumOptions?: Array<{ value: string; label: string }>;
}

/**
 * Test value for an AutoDetectField, matching the plain component it renders
 * as. Its type comes from the entity schema, not the DSL, so the wrapper calls
 * this from its preview fill handler once the schema has loaded.
 */
export function getAutoDetectFieldTestValue(
  fieldInfo: AutoDetectFieldInfo,
  rules?: ValidationRules,
  instanceIndex?: number
): Exclude<TestValueResult, { kind: 'silent-skip' }> {
  const { type, parsingType } = fieldInfo;

  // Same order the wrapper renders in: parsingType first, then the schema type.
  if (parsingType === 'date') {
    return { kind: 'value', value: isoDateDaysAgo(instanceIndex ?? 0) };
  }
  if (parsingType === 'url') {
    return {
      kind: 'value',
      value: variedScalarValue('UrlInput', instanceIndex),
    };
  }
  if (parsingType === 'fiscalYear') {
    return { kind: 'value', value: FISCAL_YEAR_TEST_VALUE };
  }
  if (parsingType === 'corporationReference') {
    return { kind: 'skip', reason: OS_ENTITY_SKIP_REASON };
  }

  if (type === 'date') {
    return { kind: 'value', value: isoDateDaysAgo(instanceIndex ?? 0) };
  }

  if (type === 'string') {
    return {
      kind: 'value',
      value: scalarTestValue('Input', rules, instanceIndex),
    };
  }
  if (type === 'number') {
    return {
      kind: 'value',
      value: variedScalarValue('NumberInput', instanceIndex),
    };
  }
  if (type === 'boolean') {
    return { kind: 'value', value: 'true' };
  }
  if (type === 'enum') {
    if (fieldInfo.multiple) {
      return { kind: 'skip', reason: 'multi-select needs a manual value' };
    }
    const firstOption = fieldInfo.enumOptions?.[0]?.value;
    return firstOption === undefined
      ? { kind: 'skip', reason: 'no options available' }
      : { kind: 'value', value: firstOption };
  }

  return { kind: 'skip', reason: `unsupported field type (${type})` };
}
